import React, { useState, useEffect } from 'react';
import { toast } from 'react-toast';
import axiosInstance from '../../api/axiosInstance';

const BitbucketSettingsModal = ({
  isOpen,
  onClose,
  onConnectSuccess,
  isEditMode = false,
  initialCredentials = null,
}) => {
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && isEditMode && initialCredentials) {
      setUsername(initialCredentials.username || '');
      setAppPassword(initialCredentials.app_password || '');
    } else {
      setUsername('');
      setAppPassword('');
    }
    setErrors({});
  }, [isOpen, isEditMode, initialCredentials]);

  const validateForm = () => {
    const newErrors = {};
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!appPassword.trim()) {
      newErrors.appPassword = 'App password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        username: username.trim(),
        app_password: appPassword.trim(),
      };

      await axiosInstance.post('/add_bitbucket_settings', payload);

      toast.success(
        isEditMode
          ? 'Bitbucket credentials updated successfully'
          : 'Bitbucket connected successfully',
        {
          position: 'top-right',
          duration: 3000,
        }
      );

      onConnectSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving Bitbucket credentials:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to save Bitbucket credentials';
      toast.error(errorMessage, {
        position: 'top-right',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setAppPassword('');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Update Bitbucket Settings' : 'Connect Bitbucket'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Username Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (errors.username) {
                  setErrors((prev) => ({ ...prev, username: '' }));
                }
              }}
              disabled={loading}
              placeholder="Enter Bitbucket username"
              className={`w-full px-3 py-2 border rounded-lg font-medium transition-colors ${
                errors.username
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
              } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            {errors.username && (
              <p className="text-red-500 text-sm mt-1">{errors.username}</p>
            )}
          </div>

          {/* App Password Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={appPassword}
              onChange={(e) => {
                setAppPassword(e.target.value);
                if (errors.appPassword) {
                  setErrors((prev) => ({ ...prev, appPassword: '' }));
                }
              }}
              disabled={loading}
              placeholder="Enter Bitbucket app password"
              className={`w-full px-3 py-2 border rounded-lg font-medium transition-colors ${
                errors.appPassword
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
              } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            {errors.appPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.appPassword}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isEditMode ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BitbucketSettingsModal;
